import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button
} from "@mui/material";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

import API from "../services/api";
import { useAuth } from "../context/AuthContext";

const NewsDetail = () => {

  const { slug } = useParams();

  const {
    token,
    user
  } = useAuth();


  // =====================================================
  // 📰 NOTICIA
  // =====================================================

  const [article, setArticle] =
    useState(null);

  const [error, setError] =
    useState(false);


  // =====================================================
  // ❤️🦊 REACCIONES
  // =====================================================

  const [likesCount, setLikesCount] =
    useState(0);

  const [foxesCount, setFoxesCount] =
    useState(0);

  const [userReaction, setUserReaction] =
    useState(null);

  const [reactionLoading, setReactionLoading] =
    useState(false);


  // =====================================================
  // 📰 CARGAR NOTICIA
  // =====================================================

  useEffect(() => {

    const fetchArticle = async () => {

      try {

        const res =
          await API.get(`/news/${slug}`);


        setArticle(res.data);


        // =================================================
        // ❤️🦊 CONTADORES
        // =================================================

        setLikesCount(
          res.data.likesCount || 0
        );

        setFoxesCount(
          res.data.foxesCount || 0
        );


        // =================================================
        // 🔥 SEO DINÁMICO
        // =================================================

        document.title =
          `${res.data.title} | Rebel Fox Gaming`;


        // Meta description

        let metaDesc =
          document.querySelector(
            "meta[name='description']"
          );


        if (!metaDesc) {

          metaDesc =
            document.createElement(
              "meta"
            );

          metaDesc.name =
            "description";

          document.head.appendChild(
            metaDesc
          );

        }


        metaDesc.setAttribute(
          "content",
          res.data.description
        );


        // =================================================
        // 🌐 OPEN GRAPH
        // =================================================

        const setMetaTag = (
          property,
          content
        ) => {

          let tag =
            document.querySelector(
              `meta[property='${property}']`
            );


          if (!tag) {

            tag =
              document.createElement(
                "meta"
              );

            tag.setAttribute(
              "property",
              property
            );

            document.head.appendChild(
              tag
            );

          }


          tag.setAttribute(
            "content",
            content || ""
          );

        };


        setMetaTag(
          "og:title",
          res.data.title
        );


        setMetaTag(
          "og:description",
          res.data.description
        );


        setMetaTag(
          "og:image",
          res.data.image
        );


        setMetaTag(
          "og:type",
          "article"
        );


      } catch (error) {

        console.error(
          "Error cargando noticia:",
          error
        );

        setError(true);

      }

    };


    fetchArticle();

  }, [slug]);


  // =====================================================
  // 👤 OBTENER REACCIÓN DEL USUARIO
  // =====================================================

  useEffect(() => {

    const fetchUserReaction = async () => {

      // Si no está logueado,
      // no necesitamos consultar nada.

      if (!token || !article) {

        setUserReaction(null);

        return;

      }


      try {

        const res =
          await API.get(
            `/news/${article._id}/reaction`,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`
              }
            }
          );


        setUserReaction(
          res.data.userReaction
        );


      } catch (error) {

        console.error(
          "Error obteniendo reacción:",
          error
        );

      }

    };


    fetchUserReaction();

  }, [token, article]);


  // =====================================================
  // ❤️🦊 CAMBIAR REACCIÓN
  // =====================================================

  const handleReaction = async (
    reaction
  ) => {

    // -----------------------------------------------
    // Usuario no logueado
    // -----------------------------------------------

    if (!token) {

      alert(
        "Tenés que iniciar sesión para reaccionar."
      );

      return;

    }


    // -----------------------------------------------
    // Evitar doble click mientras procesa
    // -----------------------------------------------

    if (reactionLoading) {
      return;
    }


    try {

      setReactionLoading(true);


      const res =
        await API.post(
          `/news/${article._id}/reaction`,
          {
            reaction
          },
          {
            headers: {
              Authorization:
                `Bearer ${token}`
            }
          }
        );


      // =================================================
      // ACTUALIZAR CONTADORES
      // =================================================

      setLikesCount(
        res.data.likesCount
      );

      setFoxesCount(
        res.data.foxesCount
      );


      // =================================================
      // ACTUALIZAR REACCIÓN DEL USUARIO
      // =================================================

      setUserReaction(
        res.data.userReaction
      );


    } catch (error) {

      console.error(
        "Error reaccionando:",
        error
      );


      alert(
        error.response?.data?.message ||
        "No se pudo registrar la reacción."
      );


    } finally {

      setReactionLoading(false);

    }

  };


  // =====================================================
  // ⏳ LOADING
  // =====================================================

  if (!article && !error) {

    return (
      <Container
        sx={{
          py: 8,
          textAlign: "center"
        }}
      >

        <Typography
          sx={{
            color: "#fff"
          }}
        >
          Cargando noticia...
        </Typography>

      </Container>
    );

  }


  // =====================================================
  // ❌ ERROR
  // =====================================================

  if (error) {

    return (
      <Container
        sx={{
          py: 8,
          textAlign: "center"
        }}
      >

        <Typography
          variant="h5"
          sx={{
            color: "#fff"
          }}
        >
          No pudimos encontrar esta noticia.
        </Typography>

      </Container>
    );

  }


  // =====================================================
  // 📰 NOTICIA
  // =====================================================

  return (

    <Box
      sx={{
        minHeight: "100vh",

        background:
          "linear-gradient(180deg, #0a0a0a, #1a1a1a)",

        py: {
          xs: 5,
          md: 8
        }
      }}
    >

      <Container
        sx={{
          maxWidth: "850px"
        }}
      >

        {/* =================================================
            TITULO
        ================================================= */}

        <Typography
          variant="h3"
          sx={{
            color: "#fff",

            fontWeight: "bold",

            mb: 2,

            textAlign: "center",

            textShadow:
              "0 0 10px rgba(255,110,0,0.4)",

            fontSize: {
              xs: "2.2rem",
              md: "3rem"
            }
          }}
        >

          {article.title}

        </Typography>


        {/* =================================================
            FECHA
        ================================================= */}

        <Typography
          sx={{
            color: "#777",

            textAlign: "center",

            mb: 4,

            fontSize: "0.9rem"
          }}
        >

          {new Date(
            article.createdAt
          ).toLocaleDateString(
            "es-AR",
            {
              day: "numeric",
              month: "long",
              year: "numeric"
            }
          )}

        </Typography>


        {/* =================================================
            IMAGEN PRINCIPAL
        ================================================= */}

        {article.image && (

          <Box
            sx={{
              mb: 5,

              overflow: "hidden",

              borderRadius: "14px",

              boxShadow:
                "0 0 25px rgba(255,110,0,0.25)"
            }}
          >

            <img
              src={article.image}
              alt={article.title}
              style={{
                width: "100%",
                display: "block",
                maxHeight: "500px",
                objectFit: "cover"
              }}
            />

          </Box>

        )}


        {/* =================================================
            CONTENIDO MARKDOWN
        ================================================= */}

        <Box
          sx={{

            color: "#ccc",

            fontSize: {
              xs: "1rem",
              md: "1.1rem"
            },

            lineHeight: 1.9,


            "& h1": {
              color: "#fff",

              fontSize: {
                xs: "2rem",
                md: "2.6rem"
              },

              marginTop: "2rem",
              marginBottom: "1rem"
            },


            "& h2": {
              color: "#ff6e00",

              fontSize: {
                xs: "1.6rem",
                md: "2rem"
              },

              marginTop: "2.5rem",
              marginBottom: "1rem"
            },


            "& h3": {
              color: "#fff",

              marginTop: "2rem",
              marginBottom: "0.8rem"
            },


            "& p": {
              marginBottom: "1.2rem"
            },


            "& strong": {
              color: "#fff"
            },


            "& a": {
              color: "#ff6e00",
              textDecoration: "none",

              "&:hover": {
                textDecoration: "underline"
              }
            },


            "& ul, & ol": {
              paddingLeft: "2rem",
              marginBottom: "1.5rem"
            },


            "& li": {
              marginBottom: "0.5rem"
            },


            "& blockquote": {
              borderLeft:
                "4px solid #ff6e00",

              margin: "2rem 0",

              paddingLeft: "1.2rem",

              color: "#aaa",

              fontStyle: "italic"
            },


            "& code": {
              backgroundColor: "#222",

              padding: "3px 6px",

              borderRadius: "4px",

              fontFamily:
                "monospace"
            },


            "& img": {

              display: "block",

              maxWidth: "100%",

              maxHeight: "500px",

              width: "auto",

              height: "auto",

              margin: "2rem auto",

              borderRadius: "12px",

              objectFit: "contain",

              boxShadow:
                "0 0 20px rgba(255,110,0,0.15)"

            },


            "& hr": {

              border: 0,

              borderTop:
                "1px solid rgba(255,255,255,0.1)",

              margin: "2.5rem 0"

            }

          }}
        >

          <ReactMarkdown
            remarkPlugins={[
              remarkGfm
            ]}
            rehypePlugins={[
              rehypeSanitize
            ]}
          >

            {article.content}

          </ReactMarkdown>

        </Box>


        {/* =================================================
            ❤️🦊 REACCIONES
        ================================================= */}

        <Box
          sx={{
            mt: 6,
            pt: 4,

            borderTop:
              "1px solid rgba(255,255,255,0.08)",

            textAlign: "center"
          }}
        >

          <Typography
            sx={{
              color: "#888",
              fontSize: "0.9rem",
              mb: 2
            }}
          >
            ¿Qué te pareció esta noticia?
          </Typography>


          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap"
            }}
          >

            {/* =================================================
                ❤️ LIKE
            ================================================= */}

            <Button
              onClick={() =>
                handleReaction("like")
              }

              disabled={reactionLoading}

              sx={{
                minWidth: "130px",

                px: 3,
                py: 1.3,

                borderRadius: "30px",

                color:
                  userReaction === "like"
                    ? "#fff"
                    : "#ccc",

                background:
                  userReaction === "like"
                    ? "rgba(255,60,80,0.2)"
                    : "rgba(255,255,255,0.05)",

                border:
                  userReaction === "like"
                    ? "1px solid rgba(255,60,80,0.6)"
                    : "1px solid rgba(255,255,255,0.1)",

                fontSize: "1rem",

                transition:
                  "all 0.25s ease",

                "&:hover": {
                  background:
                    "rgba(255,60,80,0.18)",

                  transform:
                    "translateY(-2px)"
                }
              }}
            >

              ❤️ {likesCount}

            </Button>


            {/* =================================================
                🦊 ZORRITO
            ================================================= */}

            <Button
              onClick={() =>
                handleReaction("fox")
              }

              disabled={reactionLoading}

              sx={{
                minWidth: "130px",

                px: 3,
                py: 1.3,

                borderRadius: "30px",

                color:
                  userReaction === "fox"
                    ? "#fff"
                    : "#ccc",

                background:
                  userReaction === "fox"
                    ? "rgba(255,110,0,0.25)"
                    : "rgba(255,255,255,0.05)",

                border:
                  userReaction === "fox"
                    ? "1px solid rgba(255,110,0,0.7)"
                    : "1px solid rgba(255,255,255,0.1)",

                fontSize: "1rem",

                transition:
                  "all 0.25s ease",

                "&:hover": {
                  background:
                    "rgba(255,110,0,0.2)",

                  transform:
                    "translateY(-2px)"
                }
              }}
            >

              🦊 {foxesCount}

            </Button>

          </Box>


          {/* =================================================
              🔐 MENSAJE SI NO ESTÁ LOGUEADO
          ================================================= */}

          {!user && (

            <Typography
              sx={{
                mt: 2,

                color: "#666",

                fontSize: "0.8rem"
              }}
            >
              Iniciá sesión para reaccionar.
            </Typography>

          )}


          {/* =================================================
              ✅ REACCIÓN ACTUAL
          ================================================= */}

          {userReaction === "like" && (

            <Typography
              sx={{
                mt: 2,

                color: "#777",

                fontSize: "0.8rem"
              }}
            >
              ❤️ Te gusta esta noticia
            </Typography>

          )}


          {userReaction === "fox" && (

            <Typography
              sx={{
                mt: 2,

                color: "#777",

                fontSize: "0.8rem"
              }}
            >
              🦊 Dejaste tu reacción
            </Typography>

          )}

        </Box>

      </Container>

    </Box>

  );

};

export default NewsDetail;